export type ServiceSlug =
  | 'automation'
  | 'infrastructure'
  | 'ai-systems'
  | 'network-wifi'
  | 'homelab';

export type ServiceDetail = {
  slug: ServiceSlug;
  title: string;
  shortTitle: string;
  eyebrow: string;
  summary: string;
  hero: string;
  icon: string;
  outcomes: string[];
  problems: string[];
  examples: {
    title: string;
    description: string;
    outcome: string;
  }[];
  pricing: {
    label: string;
    price: string;
    note: string;
  }[];
  faqs: {
    question: string;
    answer: string;
  }[];
};

export const featuredServices = [
  {
    title: 'Custom Software Development',
    description:
      'Internal tools, portals, dashboards, and workflow systems designed around how your operation actually works.',
    icon: 'code',
    href: '/services',
    cta: 'Plan a build'
  },
  {
    title: 'Automation Systems',
    description:
      'Lead routing, quoting, scheduling, reporting, reminders, and back-office workflows that remove manual steps.',
    icon: 'workflow',
    href: '/services/automation',
    cta: 'Automate work'
  },
  {
    title: 'Infrastructure & Networking',
    description:
      'Servers, cloud environments, network documentation, WiFi planning, backups, and operational readiness.',
    icon: 'server',
    href: '/services/infrastructure',
    cta: 'Stabilize systems'
  },
  {
    title: 'AI Business Systems',
    description:
      'Practical AI assistants, knowledge systems, intake triage, document workflows, and human-reviewed automation.',
    icon: 'brain',
    href: '/services/ai-systems',
    cta: 'Use AI safely'
  },
  {
    title: 'Managed Hosting',
    description:
      'Hosting, monitoring, backups, patching, deployments, and support for sites, portals, and internal platforms.',
    icon: 'cloud',
    href: '/pricing#hosting',
    cta: 'Compare hosting'
  },
  {
    title: 'Long-Term Support',
    description:
      'Maintenance, documentation, incremental improvements, emergency support, and fractional engineering retainers.',
    icon: 'lifeBuoy',
    href: '/pricing#support',
    cta: 'See support'
  }
];

export const serviceDetails: ServiceDetail[] = [
  {
    slug: 'automation',
    title: 'Automation Services',
    shortTitle: 'Automation',
    eyebrow: 'Business Automation Massachusetts',
    icon: 'workflow',
    summary:
      'MaytoMation builds automation systems that reduce repeated manual work, clarify handoffs, and keep business operations moving.',
    hero:
      'Automations should make work easier to trust, not harder to understand. We design systems around your current process, then document every rule, exception, and handoff so your team can operate with confidence.',
    outcomes: [
      'Faster lead response and fewer missed opportunities',
      'Cleaner handoffs between sales, operations, and billing',
      'Lower administrative overhead for repeatable tasks',
      'Reporting that shows where work is stuck'
    ],
    problems: [
      'Leads arrive through email, forms, calls, and spreadsheets with no single owner',
      'Quotes are built manually and vary by whoever creates them',
      'Scheduling requires too many texts, phone calls, and reminders',
      'Invoices, follow-ups, and status updates happen late or inconsistently'
    ],
    examples: [
      {
        title: 'Lead intake automation',
        description:
          'Centralize incoming requests, enrich them with service details, route them by geography and urgency, and notify the right person.',
        outcome: 'New inquiries move from inbox noise to owned tasks in minutes.'
      },
      {
        title: 'Contractor quote generator',
        description:
          'Turn field notes, standard pricing rules, and optional add-ons into consistent proposals that can be reviewed before sending.',
        outcome: 'Quotes are faster, clearer, and easier to maintain.'
      },
      {
        title: 'Invoice and follow-up workflow',
        description:
          'Connect completed jobs to invoice drafts, reminder sequences, and internal dashboards for overdue items.',
        outcome: 'Revenue follow-through becomes visible instead of memory-based.'
      }
    ],
    pricing: [
      { label: 'Starter Automation', price: 'Starting at $2,500', note: 'Focused workflow with one or two integrations.' },
      { label: 'Business Automation', price: 'Starting at $5,000', note: 'Multi-step workflow with routing, notifications, and reporting.' },
      { label: 'Advanced Operations Automation', price: 'Starting at $10,000', note: 'Custom logic, dashboards, approvals, and documentation.' }
    ],
    faqs: [
      {
        question: 'Do you replace our existing software?',
        answer:
          'Usually no. Many automation projects connect the tools you already use, then fill the gaps with custom logic and clear documentation.'
      },
      {
        question: 'Can you work with contractors and field service teams?',
        answer:
          'Yes. Lead intake, quoting, scheduling, job status, customer updates, and invoice workflows are common fits.'
      },
      {
        question: 'Will my team understand the system after launch?',
        answer:
          'That is a core requirement. We deliver runbooks, diagrams, and plain-language operating notes with the build.'
      }
    ]
  },
  {
    slug: 'infrastructure',
    title: 'Infrastructure Services',
    shortTitle: 'Infrastructure',
    eyebrow: 'Massachusetts Infrastructure Services',
    icon: 'server',
    summary:
      'Reliable infrastructure for websites, portals, internal tools, backups, monitoring, deployments, and business-critical systems.',
    hero:
      'Infrastructure should be quiet, observable, and recoverable. MaytoMation designs and maintains environments that are documented, monitored, and built for the people who depend on them.',
    outcomes: [
      'Clear ownership of servers, domains, DNS, SSL, backups, and deployments',
      'Documented recovery paths and admin access procedures',
      'Monitoring for uptime, performance, capacity, and failures',
      'Hosting plans that match operational risk'
    ],
    problems: [
      'No one knows where the site, app, domain, or database is hosted',
      'Backups exist in theory but have never been restored',
      'Deployments require manual steps and undocumented credentials',
      'Systems go down without alerts or clear escalation'
    ],
    examples: [
      {
        title: 'Managed application hosting',
        description:
          'Move a business portal into a monitored environment with backups, deployment notes, and recovery documentation.',
        outcome: 'The business gains a maintainable platform instead of a mystery server.'
      },
      {
        title: 'Infrastructure audit',
        description:
          'Inventory domains, hosting, DNS, users, dependencies, backups, and support risks across the organization.',
        outcome: 'Leadership gets a practical remediation plan with priority levels.'
      },
      {
        title: 'Deployment cleanup',
        description:
          'Replace fragile manual releases with repeatable deployment steps, environment notes, and rollback paths.',
        outcome: 'Updates become less stressful and easier to support.'
      }
    ],
    pricing: [
      { label: 'Infrastructure Audit', price: 'Starting at $3,500', note: 'Inventory, risk review, documentation, and roadmap.' },
      { label: 'Managed Hosting', price: 'Starting at $300/mo', note: 'Monitoring, backups, updates, and support for typical business apps.' },
      { label: 'Enterprise Infrastructure', price: 'Starting at $1,500/mo', note: 'Higher-risk systems with deeper monitoring and escalation.' }
    ],
    faqs: [
      {
        question: 'Can you take over hosting from another vendor?',
        answer:
          'Yes. We start with discovery and access review, then migrate only after the current environment and rollback plan are understood.'
      },
      {
        question: 'Do you provide emergency support?',
        answer:
          'Business-critical plans can include priority response and incident support. Scope depends on the system and risk profile.'
      },
      {
        question: 'Do you document existing systems?',
        answer:
          'Yes. Documentation projects are often the first step before improvement or migration work.'
      }
    ]
  },
  {
    slug: 'ai-systems',
    title: 'AI Services',
    shortTitle: 'AI Systems',
    eyebrow: 'Practical AI for Business',
    icon: 'brain',
    summary:
      'AI assistants, search systems, document workflows, and decision-support tools that are grounded in your business context.',
    hero:
      'Useful AI is not a novelty layer. It needs clear data boundaries, review points, access controls, and fallback behavior. We build practical AI systems that help teams move faster without losing accountability.',
    outcomes: [
      'AI knowledge assistants grounded in your procedures and documents',
      'Intake triage, draft responses, and internal search workflows',
      'Human-reviewed automations for sensitive decisions',
      'Documentation for prompts, data sources, limits, and operations'
    ],
    problems: [
      'Staff waste time searching policies, job notes, manuals, and client history',
      'AI experiments are disconnected from real workflows',
      'Sensitive information is copied into consumer tools without controls',
      'Generated answers are not reviewed, traced, or documented'
    ],
    examples: [
      {
        title: 'Internal knowledge assistant',
        description:
          'Index approved documents, SOPs, service notes, and FAQs behind a staff-only assistant with source references.',
        outcome: 'Team members find answers faster while keeping source material visible.'
      },
      {
        title: 'Lead triage assistant',
        description:
          'Summarize inbound requests, identify missing details, classify urgency, and draft follow-up questions.',
        outcome: 'Front-office staff spend less time sorting incomplete requests.'
      },
      {
        title: 'Document processing workflow',
        description:
          'Extract structured details from recurring documents, route exceptions for review, and sync approved data.',
        outcome: 'Manual copy-and-paste work becomes auditable and repeatable.'
      }
    ],
    pricing: [
      { label: 'AI Discovery Sprint', price: 'Starting at $4,000', note: 'Use-case selection, risk review, and prototype plan.' },
      { label: 'AI Knowledge Assistant', price: '$15,000+', note: 'Grounded assistant with document ingestion and review workflow.' },
      { label: 'AI Workflow System', price: '$25,000+', note: 'Production AI workflow with integrations, permissions, and monitoring.' }
    ],
    faqs: [
      {
        question: 'Will AI replace our staff?',
        answer:
          'Our focus is practical leverage: reducing repetitive search, drafting, summarizing, and routing while keeping people in control.'
      },
      {
        question: 'Can AI use our private documents?',
        answer:
          'Yes, but only with a clear access model, approved sources, and documented retention and review expectations.'
      },
      {
        question: 'Do you build chatbots?',
        answer:
          'We build assistants when they solve a real workflow problem. Sometimes the right answer is search, triage, or reporting instead of chat.'
      }
    ]
  },
  {
    slug: 'network-wifi',
    title: 'Network & WiFi Services',
    shortTitle: 'Network & WiFi',
    eyebrow: 'Massachusetts Networking Company',
    icon: 'wifi',
    summary:
      'Network cleanup, WiFi planning, rack documentation, VLANs, guest networks, camera networks, and practical site support.',
    hero:
      'A reliable network is more than an internet connection. We help businesses and property owners understand what they have, clean up what is risky, and plan networks that are easier to support.',
    outcomes: [
      'Cleaner network closets, labels, diagrams, and device inventory',
      'Better WiFi coverage planning for offices, shops, rentals, and mixed-use spaces',
      'Guest, camera, staff, and operations traffic separated where appropriate',
      'Support documentation that future technicians can actually use'
    ],
    problems: [
      'The network closet is unlabeled and no one knows what can be unplugged',
      'WiFi works in one room but fails in offices, units, or work areas',
      'Guest devices, cameras, POS, and staff computers share one flat network',
      'ISP, router, switch, and access point ownership is unclear'
    ],
    examples: [
      {
        title: 'Network documentation project',
        description:
          'Trace cabling, label ports, map equipment, document ISP details, and create a practical support packet.',
        outcome: 'Future troubleshooting starts with facts instead of guessing.'
      },
      {
        title: 'WiFi coverage refresh',
        description:
          'Review floor plans and site conditions, recommend access point placement, and coordinate configuration.',
        outcome: 'Coverage improves in the areas where staff and tenants actually work.'
      },
      {
        title: 'Small business network segmentation',
        description:
          'Separate guest, camera, and operations traffic with documented network rules and support notes.',
        outcome: 'The business gets better reliability and cleaner security boundaries.'
      }
    ],
    pricing: [
      { label: 'Network Closet Audit', price: 'Starting at $1,500', note: 'Inventory, labels, photos, diagram, and next-step plan.' },
      { label: 'WiFi Planning', price: 'Starting at $2,500', note: 'Coverage review, equipment plan, and implementation support.' },
      { label: 'Network Cleanup Project', price: 'Starting at $5,000', note: 'Documentation, configuration cleanup, and handoff.' }
    ],
    faqs: [
      {
        question: 'Do you install cabling?',
        answer:
          'We can coordinate with licensed low-voltage contractors and focus on planning, documentation, configuration, and support.'
      },
      {
        question: 'Can you support rental properties or mixed-use buildings?',
        answer:
          'Yes. Clear ownership, access, guest networks, camera networks, and documentation are especially useful in property environments.'
      },
      {
        question: 'Do you work on-site in Massachusetts?',
        answer:
          'Yes, when the project requires physical inspection, closet review, or WiFi planning.'
      }
    ]
  },
  {
    slug: 'homelab',
    title: 'Homelab Services',
    shortTitle: 'Homelab',
    eyebrow: 'Serious Home Infrastructure',
    icon: 'home',
    summary:
      'Design, cleanup, documentation, and support for advanced home networks, labs, media systems, backups, and self-hosted services.',
    hero:
      'Some homes run like small businesses: racks, VLANs, cameras, NAS devices, media servers, remote access, backups, and automation. We help make those systems reliable, documented, and recoverable.',
    outcomes: [
      'Cleaner home rack and network documentation',
      'Reliable remote access and backup planning',
      'Better separation for work, guest, IoT, camera, and lab traffic',
      'Supportable self-hosted services with clear recovery notes'
    ],
    problems: [
      'A home network has grown over years with no diagram or inventory',
      'Self-hosted services work until updates, certificates, or storage fail',
      'Remote access is convenient but not well controlled',
      'Backups and NAS storage are important but not tested'
    ],
    examples: [
      {
        title: 'Homelab documentation',
        description:
          'Map devices, services, IP ranges, VLANs, credentials ownership, backups, and maintenance routines.',
        outcome: 'The environment becomes easier to improve and support.'
      },
      {
        title: 'Self-hosted service cleanup',
        description:
          'Review containers, certificates, updates, storage, backups, and monitoring for important services.',
        outcome: 'The lab feels less fragile and easier to recover.'
      },
      {
        title: 'Advanced home WiFi and network plan',
        description:
          'Plan access points, segmentation, guest access, work-from-home needs, cameras, and equipment refreshes.',
        outcome: 'Home infrastructure gets business-grade clarity without overcomplication.'
      }
    ],
    pricing: [
      { label: 'Homelab Audit', price: 'Starting at $1,500', note: 'Inventory, risk notes, and documentation plan.' },
      { label: 'Home Network Cleanup', price: 'Starting at $3,500', note: 'Configuration review, labeling, diagrams, and support notes.' },
      { label: 'Self-Hosted Systems Support', price: 'Starting at $250/mo', note: 'Ongoing maintenance for agreed services.' }
    ],
    faqs: [
      {
        question: 'Is this for hobbyists or executives working from home?',
        answer:
          'Both, as long as the environment needs serious planning, documentation, and support.'
      },
      {
        question: 'Do you support consumer smart home products?',
        answer:
          'We focus on network, infrastructure, backups, remote access, and self-hosted systems rather than general consumer device setup.'
      },
      {
        question: 'Can you make my setup easier for another technician to support?',
        answer:
          'Yes. Documentation and support handoff are central to these projects.'
      }
    ]
  }
];

export const getServiceBySlug = (slug: string) => serviceDetails.find((service) => service.slug === slug);
